import React from 'react';
import { Platform, StyleSheet, TouchableHighlight, View } from 'react-native';
import { Text } from 'react-native-elements';

import { fonts, spacings } from '@views/shared/styles';
import {
  aquaMarineTransparent,
  paleOrangeTransparent,
  warmPinkTransparent,
  darkBlueTransparent,
  white,
  black,
} from '@views/shared/colors';
import {
  AreaWithStatus,
  AvailabilityInfo,
} from '@views/shared/interfaces/floorplan';
import { STATUS, SLOT_STATUS } from '@views/shared/TimeSlots/helper';
import { getBookingTypeByAreaType, getBookingIcon } from '@views/shared/consts';
import { SlotUIStatus, Status } from '@views/shared/TimeSlots/interfaces';
import { CustomIcon } from '@views/shared/CustomIcon';

const { extraSmall } = spacings;
const { sizeSmaller, sizeSmall } = fonts;

/**
 * This Function map the Status of a Point into the UI status type
 **/
function mapPointStatusToUI(status: Status): SlotUIStatus {
  const mapStatus = {
    [STATUS.BLOCKED]: SLOT_STATUS.BLOCKED,
    [STATUS.BOOKED]: SLOT_STATUS.BOOKED,
    [STATUS.BOOKED_BY_COLLEAGUE]: SLOT_STATUS.BOOKED_BY_COLLEAGUE,
    [STATUS.FREE]: SLOT_STATUS.FREE,
    // EXPIRED At the moment is not used
    // OUTSIDE_BUILDING is not a Status that is not showed via Floor Plan
  };

  return mapStatus[status];
}

const mapBackgroundColor = {
  blocked: warmPinkTransparent,
  booked: darkBlueTransparent,
  bookedByColleague: paleOrangeTransparent,
  free: aquaMarineTransparent,
};

const mapTextColor = {
  blocked: black,
  booked: white,
  bookedByColleague: black,
  free: black,
};

const styles = StyleSheet.create({
  area: {
    position: 'absolute',
    zIndex: 60,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  caption: {
    color: black,
    textAlign: 'center',
    fontFamily: 'mrt-semi-bold',
    alignSelf: 'center',
    padding: extraSmall,
  },
  icon: {
    textAlign: 'center',
    alignSelf: 'center',
  },
});

export function Area({
  area,
  bookArea,
  scale,
  showAvailability,
}: {
  area: AreaWithStatus;
  bookArea: (area: AreaWithStatus) => any;
  scale: number;
  showAvailability: (info?: AvailabilityInfo) => any;
}) {
  const {
    coordinates: { x, y, width, height },
    id,
    isAvailableToBook,
    name,
    status,
    areaType,
  } = area;

  const uiStatus = mapPointStatusToUI(status);
  const backgroundColor = mapBackgroundColor[uiStatus];
  const textColor = mapTextColor[uiStatus];
  const isAndroid = Platform.OS === 'android';
  const onlyIcon = scale <= (isAndroid ? 0.4 : 0.35);
  const fontSize = (isAndroid ? sizeSmaller : sizeSmall) / scale;
  const iconSize = width > 65 ? 55 : width - 10;

  const areaStyle = {
    width,
    height,
    backgroundColor,
  };

  return (
    <TouchableHighlight
      key={id}
      onPress={() =>
        isAvailableToBook
          ? bookArea(area)
          : showAvailability({ type: 'area', element: area })
      }
      style={[
        styles.area,
        {
          width,
          height,
          transform: [
            {
              translateX: x,
            },
            {
              translateY: y,
            },
          ],
        },
      ]}
    >
      <View style={[styles.area, areaStyle]}>
        {
          // areaId for debugging
          Platform.OS === 'web' && (
            <Text style={{ display: 'none' }}>{`AreaID: ${id}`}</Text>
          )
        }
        <CustomIcon
          name={getBookingIcon(getBookingTypeByAreaType(areaType))}
          size={iconSize}
          color={textColor}
          style={styles.icon}
        />
        {!onlyIcon && (
          <Text
            style={[styles.caption, { fontSize: fontSize, color: textColor }]}
          >
            {name}
          </Text>
        )}
      </View>
    </TouchableHighlight>
  );
}
